import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import * as React from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { ComboBox, useComboBoxState } from '../combo-box'
import { getExtendedDialogState } from '../dialog'
import { InputTheme } from '../input'
import { List, ListItem, useListState } from '../list'
import { INSTRUMENT_CATEGORIES } from './constants'
import { SearchError } from './search-error'
import { SearchList } from './search-list'
import { getRedirectForTicker } from './util'

interface SearchProps {
  autoFocus?: boolean
  defaultValue?: string
  theme?: keyof typeof InputTheme
}

export function Search(props: SearchProps) {
  const { reset } = useQueryErrorResetBoundary()
  const state = useComboBoxState({ minValueLength: 1, inputValue: props.defaultValue, placement: 'bottom-end' })
  const { isFullyClosed } = getExtendedDialogState(state)
  const listState = useListState({ orientation: 'vertical' })
  const [category, setCategory] = React.useState(0)

  const changeCategory = React.useRef((ev: React.KeyboardEvent) => {
    switch (ev.key) {
      case 'ArrowRight':
        state.first()
        break
      case 'ArrowUp':
        setCategory((state) => (state - 1 + INSTRUMENT_CATEGORIES.length) % INSTRUMENT_CATEGORIES.length)
        break
      case 'ArrowDown':
        setCategory((state) => (state + 1) % INSTRUMENT_CATEGORIES.length)
        break
    }
  })

  /**
   * The search box can be child of a modal/dialog so we need to handle ESC
   * otherwise it would close both the combobox and the parent.
   */
  const onInputKeyDown = React.useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && state.visible) {
        ev.stopPropagation()
        state.hide()
      }
    },
    // We only use `visible` and `hide` and don’t need the full state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.visible, state.hide]
  )

  /**
   * Reset to category "All" when the combobox closes
   */
  React.useEffect(() => {
    if (isFullyClosed) {
      setCategory(0)
    }
  }, [isFullyClosed])

  return (
    <ComboBox
      state={state}
      className="h-64 w-full sm:h-auto"
      aria-label="Search"
      inputProps={{
        theme: props.theme,
        autoFocus: props.autoFocus,
        placeholder: 'Search symbol',
        leftContent: 'search',
        autoComplete: 'off',
        autoCorrect: 'off',
        autoCapitalize: 'off',
        onKeyDown: onInputKeyDown,
      }}
      popoverProps={{ className: 'w-full sm:w-145 px-2 sm:px-0' }}
      onChange={(value) => {
        const redirectUri = getRedirectForTicker(value)
        window.location.href = redirectUri
      }}
    >
      <div className="flex flex-1 flex-col space-y-2 overflow-hidden sm:flex-row sm:space-x-2 sm:space-y-0">
        <List
          aria-label="Instrument Categories"
          includeBaseStyles={false}
          className="flex flex-row sm:w-40 sm:flex-col"
          state={listState}
          selectedIndex={category}
          onChange={(index) => setCategory(index)}
          onKeyDown={changeCategory.current}
        >
          {INSTRUMENT_CATEGORIES.map((category) => (
            <ListItem key={category.name}>{category.name}</ListItem>
          ))}
        </List>
        {(state.visible || state.animating) && (
          <ErrorBoundary
            FallbackComponent={SearchError}
            onReset={reset}
            resetKeys={[state.currentValue, state.inputValue]}
          >
            <SearchList state={state} instrument={INSTRUMENT_CATEGORIES[category].instrument} />
          </ErrorBoundary>
        )}
      </div>
    </ComboBox>
  )
}
